import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import {
  getItem,
  item,
  getHsns,
  upload_item,
  downloadItemSample,
  getItemById as fetchItemById,
  updateItem as editItem,
  updateItemStatus as changeItemStatus,
} from "./itemAPI";

const initialState = {
  loading: false,
  uploadLoading: false,
  itemsData: [],
  selectedItem: null,
  hsnList: [],
  pagination: {},
};

const actions = {
  GETITEMS: "getItems/GETITEMS",
  GETITEMBYID: "getItemById/GETITEMBYID",
  CREATEITEM: "createItem/CREATEITEM",
  UPDATEITEM: "updateItem/UPDATEITEM",
  UPDATEITEMSTATUS: "updateItemStatus/UPDATEITEMSTATUS",
  GETHSNLIST: "getHsnList/GETHSNLIST",
  ITEMUPLOADEXCEL: "item_upload_excel/ITEMUPLOADEXCEL",
  DOWNLOAD_ITEM_SAMPLE: "item/DOWNLOAD_ITEM_SAMPLE",
};

export const createItem = createAsyncThunk(
  actions.CREATEITEM,
  async (payload) => {
    const response = await item(payload);
    return response;
  }
);

export const getItemById = createAsyncThunk(
  actions.GETITEMBYID,
  async (id) => {
    const response = await fetchItemById(id);
    return response;
  }
);

export const updateItem = createAsyncThunk(
  actions.UPDATEITEM,
  async ({ id, data }) => {
    const response = await editItem(id, data);
    return response;
  }
);

export const updateItemStatus = createAsyncThunk(
  actions.UPDATEITEMSTATUS,
  async ({ id, status, status_change_reason }) => {
    const response = await changeItemStatus(id, { status, status_change_reason });
    return response;
  }
);

export const getHsnList = createAsyncThunk(
  actions.GETHSNLIST,
  async (payload) => {
    const response = await getHsns(payload);
    return response;
  }
);

export const getItems = createAsyncThunk(actions.GETITEMS, async (payload) => {
  const response = await getItem(
    payload?.page,
    payload?.per_page,
    payload?.search ? payload?.search : ""
  );
  return response;
});

export const item_upload_excel = createAsyncThunk(
  actions.ITEMUPLOADEXCEL,
  async (payload) => {
    const response = await upload_item(payload);
    return response;
  }
);

export const downloadItemSampleThunk = createAsyncThunk(
  actions.DOWNLOAD_ITEM_SAMPLE,
  async () => {
    const response = await downloadItemSample();
    return response;
  }
);

export const itemSlice = createSlice({
  name: "item",
  initialState,
  reducers: {
    resetSelectedItem: (state) => {
      state.selectedItem = null;
    },
  },
  extraReducers: (builder) => {
    // Create Item
    builder
      .addCase(createItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(createItem.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        state.loading = false;
        if (success) {
          message.success(msg);
        } else {
          message.error(msg);
        }
      })
      .addCase(createItem.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.loading = false;
        message.error(msg);
      });

    // Get Item By ID
    builder
      .addCase(getItemById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getItemById.fulfilled, (state, action) => {
        const { success, data, message: msg } = action.payload || {};
        state.loading = false;
        if (success) {
          state.selectedItem = data;
        } else {
          message.error(msg);
        }
      })
      .addCase(getItemById.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    // Update Item
    builder
      .addCase(updateItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateItem.fulfilled, (state, action) => {
        const { success, message: msg, data } = action.payload || {};
        state.loading = false;
        if (success) {
          state.selectedItem = data;
          message.success(msg || "Item updated successfully");
        } else {
          message.error(msg);
        }
      })
      .addCase(updateItem.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    // Update Item Status
    builder
      .addCase(updateItemStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateItemStatus.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload || {};
        state.loading = false;
        if (success) {
          message.success(msg || "Status updated successfully");
        } else {
          message.error(msg);
        }
      })
      .addCase(updateItemStatus.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    // HSN List
    builder
      .addCase(getHsnList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getHsnList.fulfilled, (state, action) => {
        const { success, message: msg, data } = action.payload;
        state.loading = false;
        if (success) {
          state.hsnList = data;
        } else {
          message.error(msg);
        }
      })
      .addCase(getHsnList.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.loading = false;
        message.error(msg);
      });

    // Get Items List
    builder
      .addCase(getItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(getItems.fulfilled, (state, action) => {
        const {
          success,
          message: msg,
          data: { data, pagination } = {},
        } = action.payload || {};
        state.loading = false;
        state.itemsData = data || [];
        state.pagination = pagination || {};
        if (!success && msg) {
          message.error(msg);
        }
      })
      .addCase(getItems.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.loading = false;
        message.error(msg);
      });

    // Bulk Upload Excel
    builder
      .addCase(item_upload_excel.pending, (state) => {
        state.uploadLoading = true;
      })
      .addCase(item_upload_excel.fulfilled, (state, action) => {
        state.uploadLoading = false;
        if (action.payload?.success) {
          message.success(action.payload?.message || "Items uploaded successfully!");
        } else if (action.payload?.success === false) {
          message.error(action.payload?.message || "Failed to upload items.");
        }
      })
      .addCase(item_upload_excel.rejected, (state, action) => {
        const { message: msg } = action.error;
        state.uploadLoading = false;
        message.error(msg);
      });
  },
});

export const { resetSelectedItem } = itemSlice.actions;

export default itemSlice.reducer;

